import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { nameOnlyFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, Button } from '../../components/ui';
import { SkeletonTable } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import { translateError } from '../../utils/errorMessages';
import ActivityDeleteBlockedModal, { IDeleteBlockers } from '../../components/ActivityDeleteBlockedModal';

interface IActivity {
  _id: string;
  name: string;
  description: string;
  visibility: 'public' | 'internal' | 'hidden';
  joinPolicy: 'open' | 'approval';
  sortOrder: number;
  memberCount: number;
  templateCount: number;
}

interface IActivityUsage {
  current: number;
  limit: number; // -1 = unlimited
  percentUsed: number;
}

const ActivitiesListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [activities, setActivities] = useState<IActivity[]>([]);
  const [activityUsage, setActivityUsage] = useState<IActivityUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [blocked, setBlocked] = useState<{ activity: IActivity; blockers: IDeleteBlockers } | null>(null);
  const [blockedBusy, setBlockedBusy] = useState(false);
  const [filters, setFilters] = useState<FilterValue[]>([]);

  // Permission checks
  const canCreate = hasPermission('activities:write');
  const canEdit = hasPermission('activities:write');
  const canDelete = hasPermission('activities:delete');
  // Enrollment writes need both rights (see activityRoutes member routes).
  const canRemoveMembers = hasPermission('activities:write') && hasPermission('members:write');

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const [activitiesRes, usageRes] = await Promise.all([
        api.activities.list(),
        api.organization.getUsage().catch(() => null),
      ]);
      setActivities(activitiesRes.data as IActivity[]);
      // Plan-limit metadata is non-critical — the page still works without it.
      const usagePayload = (usageRes?.data as { usage?: { activities?: IActivityUsage } } | null)?.usage?.activities;
      if (usagePayload) {
        setActivityUsage(usagePayload);
      }
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Compute whether the create button should be disabled because the
  // activity limit has been reached. -1 = unlimited.
  const atActivityLimit = useMemo(() => {
    if (!activityUsage) return false;
    if (activityUsage.limit === -1) return false;
    return activityUsage.current >= activityUsage.limit;
  }, [activityUsage]);

  const handleDeleteClick = (activity: IActivity) => {
    if (!canDelete) return;
    setDeleteId(activity._id);
  };

  // A 409 carries the members and courses still in the way; show them instead of
  // a dead-end toast so the admin can clear them without hunting.
  const blockersFrom = (err: any): IDeleteBlockers | null => {
    const data = err?.response?.data;
    return data?.code === 'ACTIVITY_DELETE_BLOCKED' && data.blockers ? data.blockers : null;
  };

  const deleteActivity = async (activity: IActivity) => {
    try {
      await api.activities.delete(Number(activity._id));
      setBlocked(null);
      fetchActivities();
      addToast(t('activities.deleted'), 'success');
    } catch (err: any) {
      const blockers = blockersFrom(err);
      if (blockers) {
        setBlocked({ activity, blockers });
        return;
      }
      addToast(translateError(err, t, 'common.error'), 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    const activity = activities.find((a) => String(a._id) === String(deleteId));
    if (!activity) return;
    setDeleteLoading(true);
    try {
      await deleteActivity(activity);
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const handleRemoveBlockedMembers = async (memberIds?: number[]) => {
    if (!blocked) return;
    setBlockedBusy(true);
    try {
      const { data } = await api.activities.removeMembers(Number(blocked.activity._id), memberIds);
      const removed = (data as { removed: number }).removed;
      addToast(
        removed === 1
          ? t('activities.removeMemberSuccess')
          : t('activities.removeAllMembersSuccess', { count: removed }),
        'success',
      );
      const fresh = await api.activities.deleteBlockers(Number(blocked.activity._id));
      setBlocked({ activity: blocked.activity, blockers: fresh.data as IDeleteBlockers });
      fetchActivities();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setBlockedBusy(false);
    }
  };

  const handleBlockedDelete = async () => {
    if (!blocked) return;
    setBlockedBusy(true);
    try {
      await deleteActivity(blocked.activity);
    } finally {
      setBlockedBusy(false);
    }
  };

  const getVisibilityBadge = (visibility: string) => {
    const styles: Record<string, React.CSSProperties> = {
      public: {
        display: 'inline-block',
        padding: '0.2rem 0.6rem',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 500,
        backgroundColor: 'rgba(40, 167, 69, 0.15)',
        color: '#28a745',
      },
      internal: {
        display: 'inline-block',
        padding: '0.2rem 0.6rem',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 500,
        backgroundColor: 'rgba(0, 123, 255, 0.15)',
        color: '#007bff',
      },
      hidden: {
        display: 'inline-block',
        padding: '0.2rem 0.6rem',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 500,
        backgroundColor: 'rgba(108, 117, 125, 0.15)',
        color: '#6c757d',
      },
    };

    const labelMap: Record<string, string> = {
      public: t('activities.visibilityPublic'),
      internal: t('activities.visibilityInternal'),
      hidden: t('activities.visibilityHidden'),
    };

    return <span style={styles[visibility] || styles.hidden}>{labelMap[visibility] || visibility}</span>;
  };

  const columns: Column<IActivity>[] = useMemo(() => [
    {
      header: t('activities.name'),
      key: 'name',
      sortValue: (activity) => activity.name ?? null,
    },
    {
      header: t('activities.visibility'),
      render: (activity) => getVisibilityBadge(activity.visibility),
      sortValue: (activity) => activity.visibility ?? null,
    },
    {
      header: t('activities.joinPolicy'),
      render: (activity) =>
        activity.joinPolicy === 'open'
          ? t('activities.joinPolicyOpen')
          : t('activities.joinPolicyApproval'),
      sortValue: (activity) => activity.joinPolicy ?? null,
    },
    {
      header: t('activities.templateCount'),
      render: (activity) => String(activity.templateCount ?? 0),
      sortValue: (activity) => activity.templateCount ?? 0,
    },
    {
      header: t('activities.memberCount'),
      render: (activity) => String(activity.memberCount ?? 0),
      sortValue: (activity) => activity.memberCount ?? 0,
    },
    {
      header: t('activities.sortOrder'),
      key: 'sortOrder',
      sortValue: (activity) => activity.sortOrder ?? null,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const filteredActivities = useMemo(() => applyFilters(activities, filters), [activities, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  // The "Create activity" affordance is rendered conditionally:
  //  - permission missing -> hidden entirely.
  //  - plan limit reached -> disabled Button with the upgrade tooltip
  //    (matches the pattern used elsewhere when a plan cap is hit).
  //  - otherwise -> the normal Link-styled primary button.
  const createAction = canCreate
    ? atActivityLimit
      ? (
          <Button
            variant="primary"
            disabled
            title={t('activities.upgradeForMoreTooltip')}
          >
            {t('activities.create')}
          </Button>
        )
      : <Link to="/admin/activities/create" className="btn btn--primary btn--md">{t('activities.create')}</Link>
    : undefined;

  return (
    <div className="admin-list-page">
      <PageHeader
        title={t('activities.title')}
        action={createAction}
      />

      {atActivityLimit && (
        <p className="empty-state__description" style={{ marginBottom: '0.75rem' }}>
          {t('activities.upgradeForMore')}
        </p>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={6} />
      ) : activities.length === 0 ? (
        <EmptyState
          title={t('activities.emptyTitle')}
          description={t('activities.emptyDescription')}
          action={canCreate && !atActivityLimit ? { label: t('activities.create'), href: '/admin/activities/create' } : undefined}
        />
      ) : (
        <>
          <FilterBar fields={nameOnlyFields} onFilterChange={handleFilterChange} />
          <DataTable
            data={filteredActivities}
            columns={columns}
            editLink={canEdit ? (activity) => `/admin/activities/${activity._id}/edit` : undefined}
            onDelete={canDelete ? handleDeleteClick : undefined}
          />
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('activities.deleteTitle')}
        message={t('activities.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
        requireConfirmText={
          activities.find((a) => String(a._id) === String(deleteId))?.name
        }
        confirmPrompt={t('activities.deleteTypeName', {
          name: activities.find((a) => String(a._id) === String(deleteId))?.name || '',
        })}
      />

      {blocked && (
        <ActivityDeleteBlockedModal
          isOpen
          activityName={blocked.activity.name}
          blockers={blocked.blockers}
          busy={blockedBusy}
          canRemoveMembers={canRemoveMembers}
          onClose={() => setBlocked(null)}
          onRemoveMembers={handleRemoveBlockedMembers}
          onDelete={handleBlockedDelete}
        />
      )}
    </div>
  );
};

export default ActivitiesListPage;
